import React from "react";
import {
  Box,
  Button,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { SectionCard } from "../styles/StyledComponents";

function UploadSection({
  uploadedFile,
  isAnalyzing,
  analysisSuccess,
  analysisError,
  onFileDrop,
  onFileSelect,
  onDragOver,
  onRemoveFile,
  onManualEntry,
  onOpenDialog,
}) {
  return (
    <SectionCard sx={{ mb: 4 }}>
      <CardContent sx={{ p: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          Carica Documenti
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Carica la tua bolletta per un'analisi automatica con AI, oppure
          inserisci i dati manualmente
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box
              onDrop={onFileDrop}
              onDragOver={onDragOver}
              sx={{
                border: 2,
                borderStyle: "dashed",
                borderColor: uploadedFile ? "success.main" : "primary.main",
                borderRadius: 3,
                p: 4,
                textAlign: "center",
                bgcolor: uploadedFile ? "success.lighter" : "primary.lighter",
                transition: "all 0.3s ease",
                cursor: "pointer",
                "&:hover": {
                  bgcolor: uploadedFile ? "success.light" : "primary.light",
                  borderColor: uploadedFile ? "success.dark" : "primary.dark",
                },
              }}
              onClick={() => document.getElementById("file-upload").click()}
            >
              <input
                id="file-upload"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={onFileSelect}
                style={{ display: "none" }}
              />

              {isAnalyzing ? (
                <Box>
                  <CircularProgress size={60} sx={{ mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Analisi in corso...
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    La nostra AI sta analizzando la tua bolletta
                  </Typography>
                </Box>
              ) : uploadedFile ? (
                <Box>
                  <CheckCircleRoundedIcon
                    sx={{ fontSize: 60, color: "success.main", mb: 2 }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {analysisSuccess
                      ? "Analisi completata!"
                      : "File caricato con successo!"}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {uploadedFile.name}
                  </Typography>
                  {analysisSuccess && (
                    <Typography
                      variant="body2"
                      color="success.main"
                      sx={{ mb: 2, fontWeight: 600 }}
                    >
                      ✓ I dati sono stati estratti e compilati automaticamente
                    </Typography>
                  )}
                  {analysisError && (
                    <Typography
                      variant="body2"
                      color="error.main"
                      sx={{ mb: 2, fontWeight: 600 }}
                    >
                      ✗ {analysisError}
                    </Typography>
                  )}
                  <Stack direction="row" spacing={2} justifyContent="center">
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveFile();
                      }}
                    >
                      Rimuovi file
                    </Button>
                    {analysisSuccess && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenDialog();
                        }}
                        endIcon={<ArrowForwardRoundedIcon />}
                      >
                        Continua
                      </Button>
                    )}
                  </Stack>
                </Box>
              ) : (
                <Box>
                  <CloudUploadRoundedIcon
                    sx={{ fontSize: 60, color: "primary.main", mb: 2 }}
                  />
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, mb: 1, color: "primary.main" }}
                  >
                    Clicca per caricare
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    o trascina qui
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    PDF, JPG, PNG (Max 10MB)
                  </Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ mt: 3, textAlign: "center" }}>
              <Chip
                icon={<AutoAwesomeRoundedIcon />}
                label="Analisi automatica con AI"
                color="primary"
                sx={{ fontWeight: 600 }}
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }}>
              <Chip label="OPPURE" />
            </Divider>
          </Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                p: 4,
                border: 1,
                borderColor: "divider",
                borderRadius: 3,
                bgcolor: "background.default",
              }}
            >
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, mb: 1, textAlign: "center" }}
              >
                Inserimento Manuale
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 3, textAlign: "center" }}
              >
                Preferisci inserire i dati manualmente?
              </Typography>
              <Button
                variant="outlined"
                size="large"
                onClick={onManualEntry}
                startIcon={<EditRoundedIcon />}
                sx={{
                  py: 2,
                  px: 4,
                  fontSize: 16,
                  fontWeight: 600,
                  borderWidth: 2,
                  "&:hover": {
                    borderWidth: 2,
                  },
                }}
              >
                Inserimento Manuale
              </Button>

              <Box sx={{ mt: 4, textAlign: "center" }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Vantaggi dell'inserimento manuale:
                </Typography>
                <Stack spacing={1} alignItems="flex-start">
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <CheckCircleRoundedIcon
                      sx={{ fontSize: 20, color: "success.main" }}
                    />
                    <Typography variant="body2">
                      Controllo completo sui dati
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <CheckCircleRoundedIcon
                      sx={{ fontSize: 20, color: "success.main" }}
                    />
                    <Typography variant="body2">
                      Processo guidato passo passo
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <CheckCircleRoundedIcon
                      sx={{ fontSize: 20, color: "success.main" }}
                    />
                    <Typography variant="body2">
                      Nessun documento richiesto
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </SectionCard>
  );
}

export default UploadSection;
